import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Countdown from "@/components/Countdown";
import { formatDate, formatShortDate } from "@/lib/helpers";
import { MapPin, CalendarDays, Plane, Clock, Loader2, Compass } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_HERO = "https://images.unsplash.com/photo-1608904872226-19d69391c761?q=85&w=2000";

const Section = ({ children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function SharedItinerary() {
  const { token } = useParams();
  const [trip, setTrip] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    api
      .get(`/public/itineraries/${token}`)
      .then(({ data }) => setTrip(data))
      .catch(() => setTrip(null));
  }, [token]);

  if (trip === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background relative z-10">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (trip === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-background relative z-10" data-testid="share-unavailable">
        <Compass className="w-10 h-10 text-primary mb-6" />
        <h1 className="font-serif text-4xl font-light tracking-tighter mb-3">Trip unavailable</h1>
        <p className="text-muted-foreground max-w-md">This shared link is no longer active. Please ask for an updated link.</p>
      </div>
    );
  }

  const coverSrc = trip.has_cover ? `${BACKEND_URL}/api/public/itineraries/${token}/cover` : FALLBACK_HERO;

  return (
    <div className="min-h-screen bg-background relative z-10">
      <div className="relative h-[85vh] min-h-[560px] w-full overflow-hidden" data-testid="share-hero">
        <img src={coverSrc} alt={trip.destination} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-background" />
        <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
          <img src="/logo.png" alt="Travel Mor" className="w-10 h-10 object-contain" />
          <span className="font-serif text-lg tracking-tighter text-white">Travel Mor</span>
        </div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-primary text-xs sm:text-sm tracking-[0.35em] uppercase mb-4 flex items-center justify-center gap-2">
              <Plane className="w-4 h-4" /> {trip.destination || "Shared Trip"}
            </p>
            <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl font-light tracking-tighter text-white mb-3">{trip.title}</h1>
            {(trip.start_date || trip.end_date) && (
              <p className="text-muted-foreground tracking-wide mb-12">
                {formatShortDate(trip.start_date)}
                {trip.end_date ? ` — ${formatShortDate(trip.end_date)}` : ""}
              </p>
            )}
            <div className="glass rounded-2xl px-6 sm:px-14 py-8 sm:py-10 inline-block">
              <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6">Countdown to departure</p>
              <Countdown startDate={trip.start_date} />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-16 pb-28">
        {(trip.days || []).length > 0 && (
          <>
            <Section>
              <p className="text-primary text-xs tracking-[0.3em] uppercase mb-2">Day by day</p>
              <h2 className="font-serif text-4xl font-light tracking-tighter mb-12">The Itinerary</h2>
            </Section>
            <div className="relative border-l border-border ml-3 sm:ml-4">
              {trip.days.map((day, idx) => (
                <Section key={idx} className="relative pl-8 sm:pl-12 pb-14">
                  <div className="absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full bg-primary gold-glow" />
                  <div className="glass rounded-xl p-6 sm:p-8" data-testid={`share-day-${idx}`}>
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-1">
                      <span className="text-xs tracking-[0.2em] uppercase text-primary">Day {idx + 1}</span>
                      {day.date && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" /> {formatDate(day.date)}
                        </span>
                      )}
                    </div>
                    <h3 className="font-serif text-2xl sm:text-3xl font-light tracking-tighter text-white">{day.title}</h3>
                    {day.destination && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                        <MapPin className="w-3.5 h-3.5 text-primary" /> {day.destination}
                      </p>
                    )}
                    {(day.activities || []).length > 0 && (
                      <div className="mt-6 space-y-4">
                        {day.activities.map((a, ai) => (
                          <div key={ai} className="flex gap-4">
                            <div className="text-primary/70 text-xs tabular-nums w-14 text-right pt-1">
                              {a.time || <Clock className="w-3.5 h-3.5 inline" />}
                            </div>
                            <div className="border-l border-border pl-4 flex-1">
                              <p className="text-foreground">{a.title}</p>
                              {a.notes && <p className="text-sm text-muted-foreground mt-0.5">{a.notes}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Section>
              ))}
            </div>
          </>
        )}

        {trip.notes && (
          <Section className="glass rounded-xl p-8 mt-6">
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-2">A note</p>
            <p className="text-foreground/90 leading-relaxed whitespace-pre-line">{trip.notes}</p>
          </Section>
        )}

        <p className="text-center text-xs text-muted-foreground mt-16 tracking-widest uppercase">Curated with Travel Mor</p>
      </div>
    </div>
  );
}
